import { EmptyState } from '../Kit/_index';
import { onBuildTicks, onFormatShort, onResolveMax } from './_chartScale';

const BLOCK = 'LStudio__app_chart';

const VIEW_W = 600;
const VIEW_H = 220;

/*
    🔥 GRÁFICO DE BARRAS

    data: [{ label: 'Ene', value: 240, tone: 'accent' }, …]

    ⚠️ EL EJE ARRANCA EN CERO, SIEMPRE

    Cortar el eje exagera las diferencias: una serie que va de 98 a 102
    parece triplicarse. En un dashboard donde alguien toma decisiones,
    eso no es un detalle estético.

    ⚠️ EL SVG NO ES LA ACCESIBILIDAD

    Un lector de pantalla no lee barras. Por eso va una <table> oculta
    con los mismos datos: se lee bien, no se ve, y no duplica nada
    porque sale del mismo array.
*/

export const ChartBar = ({
    data = [], tone = 'accent', // tono por defecto de las barras
    max = null,  height = 220, showGrid = true,  showValues = false,
    formatValue = onFormatShort, emptyLabel = 'Sin datos para graficar',  ariaLabel = 'Gráfico de barras',
}) => {

    if (!data.length) return <EmptyState title={emptyLabel} size="sm" />;

    const VALUES = data.map((d) => Number(d.value) || 0);

    const MAX = onResolveMax(VALUES, max);

    const TICKS = onBuildTicks(MAX, 4);

    // 🔥 Deja hueco entre barras sin que desaparezcan cuando hay muchas
    const SLOT = VIEW_W / data.length;
    const BAR_W = Math.max(3, Math.min(48, SLOT * 0.6));

    return (
        <div className={BLOCK} data-tone={tone} style={{ '--LS__chart_h': `${height}px` }}>

            <div className={`${BLOCK}__plot`}>

                {
                    showGrid && (
                        <div className={`${BLOCK}__axis`} aria-hidden="true">
                            {
                                [...TICKS].reverse().map((tick) => (
                                    <span key={tick} className={`${BLOCK}__axis_tick`}>
                                        {formatValue(tick)}
                                    </span>
                                ))
                            }
                        </div>
                    )
                }

                <svg className={`${BLOCK}__svg`} viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
                    preserveAspectRatio="none" role="img" aria-label={ariaLabel} >

                    {
                        showGrid && TICKS.map((tick) => {
                            const Y = VIEW_H - (tick / MAX) * VIEW_H;
                            return (
                                <line key={tick} className={`${BLOCK}__grid`}  x1="0" x2={VIEW_W} y1={Y} y2={Y} />
                            );
                        })
                    }

                    {
                        data.map((item, index) => {

                            const VALUE = Number(item.value) || 0;
                            const H = (VALUE / MAX) * VIEW_H;
                            const X = index * SLOT + (SLOT - BAR_W) / 2;

                            return (
                                <rect key={item.label ?? index}  className={`${BLOCK}__bar`}
                                    data-tone={item.tone ?? tone}   x={X} y={VIEW_H - H}
                                    width={BAR_W}  height={Math.max(H, VALUE > 0 ? 2 : 0)} >
                                    <title>{`${item.label}: ${formatValue(VALUE)}`}</title>
                                </rect>
                            );
                        })
                    }

                </svg>

            </div>

            <div className={`${BLOCK}__labels`} aria-hidden="true">
                {
                    data.map((item, index) => (
                        <span key={item.label ?? index} className={`${BLOCK}__label`}>
                            {showValues && (
                                <em className={`${BLOCK}__label_value`}>{formatValue(item.value)}</em>
                            )}
                            {item.label}
                        </span>
                    ))
                }
            </div>

            {/* 🔥 La versión que sí lee un lector de pantalla */}
            <table className={`${BLOCK}__sr`}>
                <caption>{ariaLabel}</caption>
                <tbody>
                    {
                        data.map((item, index) => (
                            <tr key={item.label ?? index}>
                                <th scope="row">{item.label}</th>
                                <td>{formatValue(item.value)}</td>
                            </tr>
                        ))
                    }
                </tbody>
            </table>

        </div>
    );
};
